import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import {
  LivretEleveCompetence,
  LivretEleveDomaine,
  LivretEval,
} from '../types/livret';
import livretKeys from './keys';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { updateMatiereLivretEleve } from '@teetsh/app/src/containers/Livrets/queries/updateMatiereLivretEleve';
import { useTranslation } from 'react-i18next';
import { useLSUErrorsMapping } from 'containers/Livrets/useLSUErrorsMapping';

interface Props {
  livretEleveId: string;
  livretEvalId: string;
}

export interface UpdateLivretEleveMatiereDTO {
  id: string;
  name: string;
  code: string;
  note?: number;
  remark?: string;
  domaines: LivretEleveDomaine[];
  competences?: LivretEleveCompetence[];
}

function updateLivretEleveMatiere({
  schoolyearId,
  schoolId,
  livretEleveId,
  matiere,
}: {
  schoolyearId: string;
  schoolId: string;
  livretEleveId: string;
  matiere: UpdateLivretEleveMatiereDTO;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/livrets-eleves/${livretEleveId}/matieres`;
  return request(requestURL, {
    method: 'PATCH',
    body: matiere,
  });
}

export function useUpdateLivretEleveMatiere(props: Props) {
  const { notifyError } = useNotification();
  const { livretEleveId, livretEvalId } = props;
  const { schoolyear, school } = useSchoolSchoolyear();

  const errorsMapping = useLSUErrorsMapping();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['carnet']);
  const { mutateAsync } = useMutation({
    mutationFn: (matiere: UpdateLivretEleveMatiereDTO) =>
      updateLivretEleveMatiere({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        livretEleveId,
        matiere,
      }),
    onMutate: async matiere => {
      await queryClient.cancelQueries({
        queryKey: livretKeys.livretDetail(
          schoolyear.id,
          school.id,
          livretEvalId,
        ),
      });
      const previousLivretEval = queryClient.getQueryData(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
      );
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
        (oldLivretEval: LivretEval) => ({
          ...oldLivretEval,
          livretsEleves: (oldLivretEval || []).livretsEleves.map(
            livretEleve => {
              if (livretEleve.id === livretEleveId) {
                return updateMatiereLivretEleve(
                  livretEleve,
                  matiere,
                  oldLivretEval.typePositionnement,
                  errorsMapping,
                );
              }
              return livretEleve;
            },
          ),
        }),
      );
      return { previousLivretEval };
    },
    onSettled: (n, error) => {
      if (error) {
        queryClient.invalidateQueries({
          queryKey: livretKeys.livretDetail(
            schoolyear.id,
            school.id,
            livretEvalId,
          ),
        });
      }
    },
    onError: (err, n, context) => {
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
        (context as any).previousLivretEval,
      );
      notifyError({
        title: t('carnet:messages.matiere.updateError'),
      });
    },
  });
  return mutateAsync;
}
